import { IconButton } from '@creditornot/cb-button';
import { Menu } from '@creditornot/cb-icons';
import { WoltLogo, cbSemanticCssVars, colors, semanticColors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { lighten } from 'polished';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { useVenueNavigationItems } from 'modules/experience/hooks/useVenueNavigationItems';
import { useExperienceModuleContext } from 'modules/experience-core';

import { EntitySwitcher } from './EntitySwitcher';
import { Footer } from './Footer';
import { Navigation } from './Navigation';
import { User } from './User';
import { useBusinessNavigationItems } from '../../hooks/useBusinessNavigationItems';

const SidebarContainer = styled.aside<{ isSidebarOpen: boolean; isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-width: 260px;
  width: ${({ isSidebarOpen, isMobile }) => (isSidebarOpen || isMobile ? '260px' : '68px')};
  transition: all 150ms ease-in-out;
  background-color: ${cssThemedValue({
    light: lighten(-0.035, semanticColors.light.surfaceMain),
    dark: colors.pepper100,
  })};
  z-index: 10;
  align-items: stretch;

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    margin-inline-start: ${({ isSidebarOpen }) => (isSidebarOpen ? 0 : '-260px')};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
  margin: 16px;
`;

const SidebarFooter = styled.div`
  display: flex;
  flex-direction: column;
  margin: 16px;
  gap: 4px;
`;

const StyledFooter = styled(Footer)`
  padding: 8px 16px;
`;

const WoltLogoLink = styled(Link)`
  height: 100%;
  cursor: pointer;
`;

const StyledWoltLogo = styled(WoltLogo)`
  height: 100%;
  padding: 6px;
  fill: ${cbSemanticCssVars.textPrimary};
`;

export const Sidebar = () => {
  const isMobile = useIsMobile();
  const { isSidebarOpen, setIsSidebarOpen, country } = useExperienceModuleContext();
  const businessNavigationItems = useBusinessNavigationItems();
  const venueNavigationItems = useVenueNavigationItems();

  return (
    <SidebarContainer isSidebarOpen={isSidebarOpen} isMobile={isMobile}>
      <SidebarHeader>
        {!isMobile && (
          <IconButton variant="neutral" size="small" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <Menu />
          </IconButton>
        )}
        {(isMobile || isSidebarOpen) && (
          <WoltLogoLink to={'/'}>
            <StyledWoltLogo />
          </WoltLogoLink>
        )}
      </SidebarHeader>
      <EntitySwitcher isSidebarOpen={isSidebarOpen} />
      <Navigation
        isSidebarOpen={isSidebarOpen}
        businessNavigationItems={businessNavigationItems}
        venueNavigationItems={venueNavigationItems}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <SidebarFooter>
        <User isSidebarOpen={isSidebarOpen} />
        {isSidebarOpen && <StyledFooter country={country} />}
      </SidebarFooter>
    </SidebarContainer>
  );
};
